import { StringUtils } from '@src/utils/StringUtils';
import { Exception } from '@src/types/Exception';

/**
 * Keeps track of error codes. Error codes may be registered
 * against this service.
 */
export class ErrorCodeDirectory {
    private static directory = {};

    public static registerCode(code) {
        if (code != null && code instanceof ErrorCode) {
            this.directory[code.getName()] = { ...code };
        } else {
            throw new Exception({ message: 'Expected error code to be a non-null instance of ErrorCode.' });
        }
    }

    public static getErrorByName(name) {
        return this.directory[name] === undefined ? undefined : { ...this.directory[name] };
    }

    public static hasErrorCode(name) {
        return !!this.directory[name];
    }
}

/**
 * A Prototype class used to construct an error code.
 * A basic error code consists of:
 *  - name: A unique id for the code (String)
 *  - defaultMessage: An error message associated with this error code.
 *
 * Note that the default message can contain parameters that can be used with
 * variable substitution.
 *
 * var errorCode = new ('MISSING_DATA', 'Cannot fetch catalog data for :item, please cut a ticket to :catalog_cti.')
 * messageUtils.addErrorMessage(
 *     {code: errorCode},
 *     'Unable to receive items, ', {item => 'ASIN B001', catalog_cti: 'IHM/RI/Catalog'}
 * );
 *
 * This would create a danger message with the text:
 * 'Unable to receive items, Cannot fetch catalog data for ASIN B001, please cut a ticket to IHM/RI/Catalog'.
 *
 */
export class ErrorCode {
    private name: string;
    private defaultMessage: string;

    constructor(name: string, defaultMessage: string) {
        this.name = name;
        this.defaultMessage = defaultMessage;
    }

    public getName() {
        return this.name;
    }

    public getDefaultMessage() {
        return this.defaultMessage;
    }

    public format(formatParams) {
        const message = this.getDefaultMessage();
        return StringUtils.formatWithNamedParameters(message, formatParams);
    }
}
